import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/** Searchable multi-select for WP taxonomy items (categories or tags).
 *  Shared between the Content list dispatch modal and the Content detail
 *  dispatch card so both flows present the same picker. */
export function TaxonomyPicker({
  label,
  emptyHint,
  items,
  loading,
  error,
  selected,
  onToggle,
}: {
  label: string;
  emptyHint: string;
  items: Array<{ id: number; name: string; count?: number }>;
  loading: boolean;
  error: string | null;
  selected: number[];
  onToggle: (id: number) => void;
}) {
  const [query, setQuery] = useState('');
  const filtered = query.trim()
    ? items.filter((i) => i.name.toLowerCase().includes(query.toLowerCase()))
    : items;
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {loading ? (
        <div className="text-xs text-muted-foreground">Yükleniyor…</div>
      ) : error ? (
        <div className="text-xs text-destructive">{error}</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-muted-foreground">Site'de henüz {label.toLowerCase()} yok.</div>
      ) : (
        <>
          <Input
            type="search"
            placeholder={`${label} içinde ara…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-8 text-xs"
          />
          <div className="max-h-40 overflow-y-auto rounded-md border p-2 space-y-1">
            {filtered.map((c) => {
              const on = selected.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onToggle(c.id)}
                  className={cn(
                    'w-full text-left px-2 py-1 rounded text-xs flex items-center justify-between gap-2 transition-colors',
                    on ? 'bg-primary text-primary-foreground' : 'hover:bg-accent',
                  )}
                >
                  <span className="truncate">{c.name}</span>
                  {typeof c.count === 'number' && (
                    <span
                      className={cn(
                        'text-[10px] font-mono px-1 rounded',
                        on ? 'bg-primary-foreground/20' : 'text-muted-foreground',
                      )}
                    >
                      {c.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
      <p className="text-[11px] text-muted-foreground">
        {selected.length > 0 ? `${selected.length} seçili` : emptyHint}
      </p>
    </div>
  );
}
